import React from 'react';
import { Card, Row, Col, Button, Alert, Badge } from 'react-bootstrap';

const PreviewResults = ({ previewData, onConfirm, onCancel, isLoading }) => {
    if (!previewData) return null;

    const { branch, dateRange, usersPreview, reportsPreview, summary } = previewData;

    const formatDuration = (days) => {
        if (days === 1) return '1일';
        return `${days}일`;
    };

    const StatusBadge = ({ count, type }) => {
        if (count === 0) return <Badge bg="success">{count}</Badge>;
        if (type === 'warning') return <Badge bg="warning" text="dark">{count}</Badge>;
        return <Badge bg="danger">{count}</Badge>;
    };

    return (
        <Card className="mb-4 border-info">
            <Card.Header className="bg-info text-white">
                <h5 className="mb-0">🔍 동기화 미리보기 결과</h5>
                <small>
                    {branch.name} | {dateRange.beginDate?.split('T')[0]} ~ {dateRange.endDate?.split('T')[0]} 
                    ({formatDuration(dateRange.durationDays)})
                </small>
            </Card.Header>
            <Card.Body>
                {/* Summary Alert */}
                <Alert variant={summary.actionRequired ? "warning" : "success"} className="mb-4">
                    <div className="d-flex justify-content-between align-items-center">
                        <div>
                            <strong>
                                {summary.actionRequired 
                                    ? `⚠️ ${summary.totalMissingUsers + summary.totalMissingReports + summary.totalReportsWithoutPDF}개 항목이 누락되었습니다`
                                    : '✅ 모든 데이터가 최신 상태입니다'
                                }
                            </strong>
                            {summary.actionRequired && (
                                <div className="mt-1 small">
                                    예상 소요시간: <strong>{summary.estimatedTime}</strong>
                                </div>
                            )}
                        </div>
                    </div>
                </Alert>

                {/* Detailed Status */}
                <Row className="mb-4">
                    <Col md={6}>
                        <Card className="h-100 border-primary">
                            <Card.Header className="bg-primary text-white">
                                <h6 className="mb-0">👥 사용자 상태</h6>
                            </Card.Header>
                            <Card.Body>
                                <div className="d-flex justify-content-between align-items-center mb-2">
                                    <span>Evelab 사용자:</span>
                                    <Badge bg="info">{usersPreview.evelabUsers}</Badge>
                                </div>
                                <div className="d-flex justify-content-between align-items-center mb-2">
                                    <span>기존 사용자:</span>
                                    <Badge bg="success">{usersPreview.existingUsers}</Badge>
                                </div>
                                <div className="d-flex justify-content-between align-items-center">
                                    <span>누락 사용자:</span>
                                    <StatusBadge count={usersPreview.missingUsers} type="danger" />
                                </div>
                                
                                {usersPreview.missingUsers > 0 && usersPreview.missingUsersList?.length > 0 && (
                                    <div className="mt-3">
                                        <small className="text-muted">누락된 사용자 예시:</small>
                                        <ul className="small mt-1 mb-0">
                                            {usersPreview.missingUsersList.slice(0, 3).map((user, idx) => (
                                                <li key={idx}>{user.name} ({user.birth})</li>
                                            ))}
                                            {usersPreview.missingUsersList.length > 3 && 
                                                <li>...외 {usersPreview.missingUsersList.length - 3}명</li>
                                            }
                                        </ul>
                                    </div>
                                )}
                            </Card.Body>
                        </Card>
                    </Col>
                    <Col md={6}>
                        <Card className="h-100 border-warning">
                            <Card.Header className="bg-warning text-dark">
                                <h6 className="mb-0">📊 리포트 상태</h6>
                            </Card.Header>
                            <Card.Body>
                                <div className="d-flex justify-content-between align-items-center mb-2">
                                    <span>Evelab 리포트:</span>
                                    <Badge bg="info">{reportsPreview.evelabReports}</Badge>
                                </div>
                                <div className="d-flex justify-content-between align-items-center mb-2">
                                    <span>기존 리포트:</span>
                                    <Badge bg="success">{reportsPreview.existingReports}</Badge>
                                </div>
                                <div className="d-flex justify-content-between align-items-center mb-2">
                                    <span>누락 리포트:</span>
                                    <StatusBadge count={reportsPreview.missingReports} type="danger" />
                                </div>
                                <div className="d-flex justify-content-between align-items-center mb-2">
                                    <span>PDF 누락:</span>
                                    <StatusBadge count={reportsPreview.reportsWithoutPDF} type="warning" />
                                </div>
                                {reportsPreview.reportsWithMissingUser > 0 && (
                                    <div className="d-flex justify-content-between align-items-center">
                                        <span>⚠️ 유저 누락 리포트:</span>
                                        <StatusBadge count={reportsPreview.reportsWithMissingUser} type="warning" />
                                    </div>
                                )}

                                {reportsPreview.missingReports > 0 && reportsPreview.missingReportsList?.length > 0 && (
                                    <div className="mt-3">
                                        <small className="text-muted">누락된 리포트 예시:</small>
                                        <ul className="small mt-1 mb-0">
                                            {reportsPreview.missingReportsList.slice(0, 3).map((report, idx) => (
                                                <li key={idx}>{report.rid} ({report.date})</li>
                                            ))}
                                            {reportsPreview.missingReportsList.length > 3 && 
                                                <li>...외 {reportsPreview.missingReportsList.length - 3}개</li>
                                            }
                                        </ul>
                                    </div>
                                )}
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>

                {/* Warnings */}
                {summary.warnings && summary.warnings.length > 0 && (
                    <Alert variant="warning" className="mb-3">
                        <h6>⚠️ 주의사항:</h6>
                        <ul className="mb-0">
                            {summary.warnings.map((warning, idx) => (
                                <li key={idx} className="small"><strong>{warning}</strong></li>
                            ))}
                        </ul>
                    </Alert>
                )}

                {/* Recommendations */}
                {summary.recommendations && summary.recommendations.length > 0 && (
                    <Alert variant="light" className="mb-4">
                        <h6>💡 권장사항:</h6>
                        <ul className="mb-0">
                            {summary.recommendations.map((rec, idx) => (
                                <li key={idx} className="small">{rec}</li>
                            ))}
                        </ul>
                    </Alert>
                )}

                {/* Action Buttons */}
                <Row>
                    <Col>
                        <div className="d-flex gap-2 justify-content-end">
                            <Button 
                                variant="secondary" 
                                onClick={onCancel}
                                disabled={isLoading}
                            >
                                취소
                            </Button>
                            
                            {summary.actionRequired ? (
                                <Button 
                                    variant="primary" 
                                    size="lg"
                                    onClick={onConfirm}
                                    disabled={isLoading}
                                    className="fw-bold"
                                >
                                    {isLoading ? (
                                        <>
                                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                            동기화 실행 중...
                                        </>
                                    ) : (
                                        <>🔄 동기화 실행 ({summary.estimatedTime})</>
                                    )}
                                </Button>
                            ) : (
                                <Button variant="success" disabled className="fw-bold">
                                    ✅ 동기화 불필요
                                </Button>
                            )}
                        </div>
                    </Col>
                </Row>
            </Card.Body>
        </Card>
    );
};

export default PreviewResults;